import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Clock, Check, X, ArrowLeft, User, LogIn, LogOut, Loader2, AlertTriangle, AlertCircle, Lock, Building2, ChevronDown, WifiOff, Wifi, ScanFace, KeyRound, QrCode } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useLogo, LANGUAGES, Flag, TRANSLATIONS } from "../App";
import * as faceapi from "face-api.js";
import { saveFaceData, getFaceData, savePendingAttendance, isOnline, onOnlineStatusChange, requestBackgroundSync } from "../utils/offlineStorage";

// 高精度モデル用オプション（SsdMobilenetv1）
const SSD_OPTIONS = new faceapi.SsdMobilenetv1Options({
  minConfidence: 0.5
});
// フォールバック用（TinyFaceDetector）
const TINY_OPTIONS = new faceapi.TinyFaceDetectorOptions({ 
  inputSize: 320,
  scoreThreshold: 0.5 
});

// ===== 案1: マッチング閾値の緩和 (0.5 → 0.55) =====
const MATCH_THRESHOLD = 0.48;

// ===== 案2: 連続マッチ回数を増やす (2 → 3) =====
const CONSECUTIVE_MATCHES_REQUIRED = 3;

// ===== 解像度の動的調整設定 =====
// 高スペック端末用
const HIGH_SPEC_CONFIG = {
  videoWidth: 640,
  videoHeight: 480,
  detectionInterval: 100,  // 100ms (10fps)
};
// 低スペック端末用
const LOW_SPEC_CONFIG = {
  videoWidth: 320,
  videoHeight: 240,
  detectionInterval: 200,  // 200ms (5fps)
};

// 端末スペック判定関数
const detectDevicePerformance = () => {
  // メモリチェック (4GB未満は低スペック)
  const deviceMemory = navigator.deviceMemory || 4;
  // CPUコア数チェック (4コア未満は低スペック)
  const cpuCores = navigator.hardwareConcurrency || 4;
  // モバイルデバイス判定
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // 低スペック判定
  const isLowSpec = deviceMemory < 4 || cpuCores < 4 || (isMobile && (deviceMemory < 6 || cpuCores < 6));
  
  console.log(`Device check: memory=${deviceMemory}GB, cores=${cpuCores}, mobile=${isMobile}, lowSpec=${isLowSpec}`);
  
  return isLowSpec ? 'low' : 'high';
};

// ===== 案4: 顔画像の前処理改善 =====
const preprocessFaceImage = (imageElement) => {
  const canvas = document.createElement('canvas');
  const targetSize = 150; // 正規化サイズ
  canvas.width = targetSize;
  canvas.height = targetSize;
  const ctx = canvas.getContext('2d');
  
  // 元画像を描画
  ctx.drawImage(imageElement, 0, 0, targetSize, targetSize);
  
  // 画像データを取得
  const imageData = ctx.getImageData(0, 0, targetSize, targetSize);
  const data = imageData.data;
  
  // 明るさとコントラストの自動調整
  let minBrightness = 255;
  let maxBrightness = 0;
  
  // 明るさの範囲を計算
  for (let i = 0; i < data.length; i += 4) {
    const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
    minBrightness = Math.min(minBrightness, brightness);
    maxBrightness = Math.max(maxBrightness, brightness);
  }
  
  // コントラスト伸長（ヒストグラム均等化の簡易版）
  const range = maxBrightness - minBrightness;
  if (range > 0 && range < 200) { // 明るさの幅が狭い場合のみ調整
    const factor = 220 / range;
    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.min(255, Math.max(0, (data[i] - minBrightness) * factor + 20));
      data[i + 1] = Math.min(255, Math.max(0, (data[i + 1] - minBrightness) * factor + 20));
      data[i + 2] = Math.min(255, Math.max(0, (data[i + 2] - minBrightness) * factor + 20));
    }
    ctx.putImageData(imageData, 0, 0);
    console.log(`Image preprocessed: brightness range ${minBrightness}-${maxBrightness} -> normalized`);
  }
  
  return canvas;
};

// プロフィール写真コンポーネント
const ProfilePhoto = ({ employee, size = "w-20 h-20", textSize = "text-4xl", bgColor = "from-emerald-400 to-emerald-600" }) => {
  const facePhoto = employee?.profile_photo || employee?.face_photo || employee?.face_encodings?.[0]?.image;
  
  if (facePhoto) {
    const imageSrc = facePhoto.startsWith('data:') ? facePhoto : `data:image/jpeg;base64,${facePhoto}`;
    return (
      <div className={`${size} rounded-full overflow-hidden shadow-xl mx-auto flex-shrink-0`}>
        <img 
          src={imageSrc} 
          alt={employee?.name} 
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center top' }}
        />
      </div>
    );
  }
  
  return (
    <div className={`${size} rounded-full bg-gradient-to-br ${bgColor} flex items-center justify-center ${textSize} font-bold shadow-xl mx-auto flex-shrink-0`}>
      {employee?.name?.charAt(0) || "?"}
    </div>
  );
};

const FaceAuthPage = () => {
  const navigate = useNavigate();
  const { logoUrl } = useLogo();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // 企業コードをローカルストレージまたはURLパラメータから取得
  const getSavedCompanyCode = () => {
    const urlCode = searchParams.get('company');
    if (urlCode) return urlCode;
    return localStorage.getItem('prc_company_code') || '';
  };
  
  const [companyCode, setCompanyCodeState] = useState(getSavedCompanyCode());
  const [inputCode, setInputCode] = useState(getSavedCompanyCode());
  
  // 企業コード設定時にローカルストレージにも保存
  const setCompanyCode = (code) => {
    setCompanyCodeState(code);
    if (code) {
      localStorage.setItem('prc_company_code', code);
    }
  };
  
  const [codeError, setCodeError] = useState('');
  const [checkingCode, setCheckingCode] = useState(false);
  const [selectedLang, setSelectedLang] = useState("ja");
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [result, setResult] = useState(null);
  const [currentTime, setCurrentTime] = useState("");
  const [scanStatus, setScanStatus] = useState("loading");
  const [confirming, setConfirming] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [employeeDescriptors, setEmployeeDescriptors] = useState([]);
  const [faceDetected, setFaceDetected] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const lastDetectionRef = useRef(0);

  // Get translations
  const t = TRANSLATIONS[selectedLang] || TRANSLATIONS.ja;

  // 保存された企業コードがある場合、自動で検証（初回レンダリング時のみ）
  const [autoVerified, setAutoVerified] = useState(false);
  
  useEffect(() => {
    if (autoVerified) return;
    
    const savedCode = localStorage.getItem('prc_company_code');
    const urlCode = searchParams.get('company');
    
    // URLパラメータがある場合はそちらを優先
    if (urlCode) {
      setAutoVerified(true);
      return;
    }
    
    // ローカルストレージに保存されたコードがあり、まだ検証していない場合
    if (savedCode && !companyCode) {
      const autoVerify = async () => {
        setCheckingCode(true);
        try {
          const response = await axios.get(`${API}/verify-company?company_code=${savedCode}`);
          if (response.data) {
            setCompanyCode(savedCode);
            setSearchParams({ company: savedCode });
          }
        } catch (error) {
          // 保存されたコードが無効な場合、ローカルストレージをクリア
          localStorage.removeItem('prc_company_code');
          setInputCode('');
        } finally {
          setCheckingCode(false);
          setAutoVerified(true);
        }
      };
      autoVerify();
    } else {
      setAutoVerified(true);
    }
  }, [autoVerified, companyCode, searchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const isDetectingRef = useRef(false);
  const cameraCheckIntervalRef = useRef(null);
  const lastSuccessfulDetectionRef = useRef(Date.now());
  const consecutiveMatchesRef = useRef([]);  // 連続マッチング用
  const [matchProgress, setMatchProgress] = useState(0);  // マッチング進捗表示用
  const [useSsdModel, setUseSsdModel] = useState(true);  // 高精度モデル使用フラグ
  const [online, setOnline] = useState(isOnline());  // オンライン状態
  const [performanceMode, setPerformanceMode] = useState('high');  // 'high' or 'low'
  const [deviceConfig, setDeviceConfig] = useState(HIGH_SPEC_CONFIG);  // カメラ・検出設定
  const [employeeLang, setEmployeeLang] = useState(null);  // 認識された従業員の言語（一時表示用）
  const [faceAuthFailCount, setFaceAuthFailCount] = useState(0);  // 顔認証失敗回数
  const [showFallbackOption, setShowFallbackOption] = useState(false);  // フォールバックオプション表示
  const FACE_AUTH_MAX_FAILS = 3;  // フォールバック表示までの失敗回数
  
  // 認証方法選択（URLパラメータからも取得）
  const methodParam = searchParams.get('method');
  const [authMethod, setAuthMethod] = useState(methodParam === 'pin' ? 'pin' : methodParam === 'face' ? 'face' : methodParam === 'qr' ? 'qr' : methodParam === 'qr_face' ? 'qr_face' : null);
  const [employeeCodeInput, setEmployeeCodeInput] = useState('');
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinAuthLoading, setPinAuthLoading] = useState(false);
  const [qrScanning, setQrScanning] = useState(false);
  const qrScannerRef = useRef(null);
  const [qrDetectedBox, setQrDetectedBox] = useState(null);  // QRコード検出位置
  const [qrIdentifiedEmployee, setQrIdentifiedEmployee] = useState(null);  // QRで特定された従業員
  const [capturedPhoto, setCapturedPhoto] = useState(null);  // 撮影した顔写真
  const [photoCapturing, setPhotoCapturing] = useState(false);  // 写真撮影中フラグ
  const photoVideoRef = useRef(null);  // 写真撮影用カメラ参照
  const [autoConfirmCountdown, setAutoConfirmCountdown] = useState(null);  // 自動確定カウントダウン

  // 従業員言語用の翻訳（確認・完了画面用）
  const EMPLOYEE_TRANSLATIONS = {
    ja: { 
      identityConfirm: "本人確認", 
      matchRate: "一致度", 
      clockIn: "出勤", 
      clockOut: "退勤",
      completedToday: "本日完了済",
      confirmQuestion: "打刻してよろしいですか？",
      cancel: "キャンセル",
      confirm: "確認",
      close: "閉じる",
      clockInComplete: "出勤完了",
      clockOutComplete: "退勤完了",
      myPage: "マイページ",
      nextPerson: "次の人",
      greetingIn: "おはようございます",
      greetingOut: "お疲れ様でした"
    },
    en: { 
      identityConfirm: "Identity Confirmation", 
      matchRate: "Match", 
      clockIn: "Clock In", 
      clockOut: "Clock Out",
      completedToday: "Completed Today",
      confirmQuestion: "Proceed with time record?",
      cancel: "Cancel",
      confirm: "Confirm",
      close: "Close",
      clockInComplete: "Clock In Complete",
      clockOutComplete: "Clock Out Complete",
      myPage: "My Page",
      nextPerson: "Next",
      greetingIn: "Good morning",
      greetingOut: "Good job today"
    },
    zh: { 
      identityConfirm: "身份确认", 
      matchRate: "匹配度", 
      clockIn: "上班打卡", 
      clockOut: "下班打卡",
      completedToday: "今日已完成",
      confirmQuestion: "确认打卡吗？",
      cancel: "取消",
      confirm: "确认",
      close: "关闭",
      clockInComplete: "上班打卡完成",
      clockOutComplete: "下班打卡完成",
      myPage: "我的页面",
      nextPerson: "下一位",
      greetingIn: "早上好",
      greetingOut: "辛苦了"
    },
    ko: { 
      identityConfirm: "본인 확인", 
      matchRate: "일치율", 
      clockIn: "출근", 
      clockOut: "퇴근",
      completedToday: "오늘 완료됨",
      confirmQuestion: "출퇴근을 기록하시겠습니까?",
      cancel: "취소",
      confirm: "확인",
      close: "닫기",
      clockInComplete: "출근 완료",
      clockOutComplete: "퇴근 완료",
      myPage: "마이페이지",
      nextPerson: "다음",
      greetingIn: "안녕하세요",
      greetingOut: "수고하셨습니다"
    },
    vi: { 
      identityConfirm: "Xác nhận danh tính", 
      matchRate: "Độ khớp", 
      clockIn: "Chấm công vào", 
      clockOut: "Chấm công ra",
      completedToday: "Đã hoàn thành hôm nay",
      confirmQuestion: "Xác nhận chấm công?",
      cancel: "Hủy",
      confirm: "Xác nhận",
      close: "Đóng",
      clockInComplete: "Chấm công vào hoàn tất",
      clockOutComplete: "Chấm công ra hoàn tất",
      myPage: "Trang của tôi",
      nextPerson: "Tiếp theo",
      greetingIn: "Chào buổi sáng",
      greetingOut: "Cảm ơn vì đã làm việc chăm chỉ"
    },
    pt: { 
      identityConfirm: "Confirmação de Identidade", 
      matchRate: "Correspondência", 
      clockIn: "Entrada", 
      clockOut: "Saída",
      completedToday: "Concluído Hoje",
      confirmQuestion: "Confirmar registro de ponto?",
      cancel: "Cancelar",
      confirm: "Confirmar",
      close: "Fechar",
      clockInComplete: "Entrada Registrada",
      clockOutComplete: "Saída Registrada",
      myPage: "Minha Página",
      nextPerson: "Próximo",
      greetingIn: "Bom dia",
      greetingOut: "Bom trabalho"
    },
    es: { 
      identityConfirm: "Confirmación de Identidad", 
      matchRate: "Coincidencia", 
      clockIn: "Entrada", 
      clockOut: "Salida",
      completedToday: "Completado Hoy",
      confirmQuestion: "¿Confirmar registro de tiempo?",
      cancel: "Cancelar",
      confirm: "Confirmar",
      close: "Cerrar",
      clockInComplete: "Entrada Registrada",
      clockOutComplete: "Salida Registrada",
      myPage: "Mi Página",
      nextPerson: "Siguiente",
      greetingIn: "Buenos días",
      greetingOut: "Buen trabajo"
    },
    tl: { 
      identityConfirm: "Pagkumpirma ng Pagkakakilanlan", 
      matchRate: "Tugma", 
      clockIn: "Pasok", 
      clockOut: "Labas",
      completedToday: "Tapos Na Ngayon",
      confirmQuestion: "Kumpirmahin ang time record?",
      cancel: "Kanselahin",
      confirm: "Kumpirmahin",
      close: "Isara",
      clockInComplete: "Pasok Kumpleto",
      clockOutComplete: "Labas Kumpleto",
      myPage: "Aking Pahina",
      nextPerson: "Susunod",
      greetingIn: "Magandang umaga",
      greetingOut: "Salamat sa pagtrabaho"
    },
    id: { 
      identityConfirm: "Konfirmasi Identitas", 
      matchRate: "Kecocokan", 
      clockIn: "Masuk", 
      clockOut: "Keluar",
      completedToday: "Selesai Hari Ini",
      confirmQuestion: "Konfirmasi absensi?",
      cancel: "Batal",
      confirm: "Konfirmasi",
      close: "Tutup",
      clockInComplete: "Masuk Tercatat",
      clockOutComplete: "Keluar Tercatat",
      myPage: "Halaman Saya",
      nextPerson: "Berikutnya",
      greetingIn: "Selamat pagi",
      greetingOut: "Terima kasih atas kerja kerasnya"
    },
    th: { 
      identityConfirm: "ยืนยันตัวตน", 
      matchRate: "ความตรงกัน", 
      clockIn: "เข้างาน", 
      clockOut: "ออกงาน",
      completedToday: "เสร็จสิ้นวันนี้",
      confirmQuestion: "ยืนยันการลงเวลา?",
      cancel: "ยกเลิก",
      confirm: "ยืนยัน",
      close: "ปิด",
      clockInComplete: "ลงเวลาเข้างานสำเร็จ",
      clockOutComplete: "ลงเวลาออกงานสำเร็จ",
      myPage: "หน้าของฉัน",
      nextPerson: "ถัดไป",
      greetingIn: "สวัสดีตอนเช้า",
      greetingOut: "ขอบคุณสำหรับการทำงานหนัก"
    },
    ne: { 
      identityConfirm: "पहिचान पुष्टि", 
      matchRate: "मिलान", 
      clockIn: "आगमन", 
      clockOut: "प्रस्थान",
      completedToday: "आज पूरा भयो",
      confirmQuestion: "समय रेकर्ड पुष्टि गर्नुहोस्?",
      cancel: "रद्द",
      confirm: "पुष्टि",
      close: "बन्द",
      clockInComplete: "आगमन पूरा",
      clockOutComplete: "प्रस्थान पूरा",
      myPage: "मेरो पृष्ठ",
      nextPerson: "अर्को",
      greetingIn: "शुभ प्रभात",
      greetingOut: "मेहनतको लागि धन्यवाद"
    },
    my: { 
      identityConfirm: "အထောက်အထားအတည်ပြုခြင်း", 
      matchRate: "ကိုက်ညီမှု", 
      clockIn: "အဝင်", 
      clockOut: "အထွက်",
      completedToday: "ယနေ့ပြီးဆုံးပြီ",
      confirmQuestion: "အချိန်မှတ်တမ်းအတည်ပြုမလား?",
      cancel: "ပယ်ဖျက်",
      confirm: "အတည်ပြု",
      close: "ပိတ်",
      clockInComplete: "အဝင်မှတ်တမ်းပြီး",
      clockOutComplete: "အထွက်မှတ်တမ်းပြီး",
      myPage: "ကျွန်ုပ်စာမျက်နှာ",
      nextPerson: "နောက်တစ်ယောက်",
      greetingIn: "မင်္ဂလာနံနက်ခင်းပါ",
      greetingOut: "ကြိုးစားမှုအတွက် ကျေးဇူးတင်ပါတယ်"
    }
  };

  // 現在使用する翻訳（候補者の言語 > 従業員言語 > 選択言語の優先順）
  const getDisplayLang = () => {
    // candidateがある場合はその従業員の言語を使用
    if (candidate?.employee?.language) {
      return candidate.employee.language;
    }
    // resultがある場合もその従業員の言語を使用
    if (result?.employee?.language) {
      return result.employee.language;
    }
    return employeeLang || selectedLang;
  };
  const currentLang = getDisplayLang();
  const et = EMPLOYEE_TRANSLATIONS[currentLang] || EMPLOYEE_TRANSLATIONS.ja;

  // オンライン状態の監視
  useEffect(() => {
    const unsubscribe = onOnlineStatusChange((status) => {
      setOnline(status);
      if (status) {
        toast.success("オンラインに復帰しました");
      } else {
        toast.warning("オフラインモードで動作中");
      }
    });
    return unsubscribe;
  }, []);

  // 端末スペック判定（初回のみ）
  useEffect(() => {
    const mode = detectDevicePerformance();
    setPerformanceMode(mode);
    setDeviceConfig(mode === 'low' ? LOW_SPEC_CONFIG : HIGH_SPEC_CONFIG);
    
    if (mode === 'low') {
      console.log('Low-spec device detected, using lightweight mode');
      toast.info("軽量モードで動作します", { duration: 3000 });
    }
  }, []);

  // 顔画像をキャプチャしてBase64に変換
  const captureFaceImage = (detection) => {
    if (!videoRef.current || !detection) return null;
    
    try {
      const video = videoRef.current;
      const box = detection.detection.box;
      
      // 顔の領域を少し広げてキャプチャ
      const padding = 40;
      const x = Math.max(0, box.x - padding);
      const y = Math.max(0, box.y - padding);
      const width = Math.min(box.width + padding * 2, video.videoWidth - x);
      const height = Math.min(box.height + padding * 2, video.videoHeight - y);
      
      // キャンバスを作成して顔部分をクロップ
      const captureCanvas = document.createElement('canvas');
      const targetSize = 150; // 出力サイズ
      captureCanvas.width = targetSize;
      captureCanvas.height = targetSize;
      const ctx = captureCanvas.getContext('2d');
      
      // 顔部分をキャンバスに描画（リサイズ）
      ctx.drawImage(video, x, y, width, height, 0, 0, targetSize, targetSize);
      
      // JPEG形式でBase64に変換（品質80%）
      const base64Image = captureCanvas.toDataURL('image/jpeg', 0.8);
      return base64Image;
    } catch (error) {
      console.error("Face capture error:", error);
      return null;
    }
  };

  // 企業コード入力処理
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!inputCode.trim()) {
      setCodeError('企業コードを入力してください');
      return;
    }
    
    setCheckingCode(true);
    setCodeError('');
    
    try {
      // 企業コードが有効かチェック
      const response = await axios.get(`${API}/verify-company?company_code=${inputCode.trim().toUpperCase()}`);
      if (response.data) {
        setCompanyCode(inputCode.trim().toUpperCase());
        setSearchParams({ company: inputCode.trim().toUpperCase() });
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setCodeError('企業コードが見つかりません');
      } else if (error.response?.status === 403) {
        setCodeError(error.response.data?.detail || 'この企業はQR認証を利用できません');
      } else {
        setCodeError('エラーが発生しました');
      }
    } finally {
      setCheckingCode(false);
    }
  };

  // 時刻更新
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('ja-JP', { 
        timeZone: 'Asia/Tokyo',
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit',
        hour12: false
      }));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // モデル読み込み（顔認証モードでのみ実行 - QR/PINモードではスキップ）
  useEffect(() => {
    const loadModels = async () => {
      // QR認証またはPIN認証モードでは顔認証モデルは不要
      if (authMethod === 'qr' || authMethod === 'qr_face' || authMethod === 'pin') {
        console.log("Skipping face model loading for QR/PIN auth mode");
        return;
      }
      
      try {
        const MODEL_URL = "/models";
        // 高精度モデル（SsdMobilenetv1）と従来モデルの両方を読み込み
        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL).catch(() => {
            console.log("SSD model not available, using TinyFaceDetector");
            setUseSsdModel(false);
            return null;
          }),
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
        ]);
        setModelsLoaded(true);
        setScanStatus("waiting");
        console.log("Face detection models loaded successfully");
      } catch (error) {
        console.error("Model load error:", error);
        // 顔認証モード以外ではエラーを表示しない
        if (authMethod === 'face') {
          toast.error("モデル読み込みエラー");
        }
        setScanStatus("error");
      }
    };
    loadModels();
  }, [authMethod]);

  // QR/PIN認証用の企業情報取得
  useEffect(() => {
    const loadCompanyInfo = async () => {
      if (!companyCode) return;
      // QR認証またはPIN認証モードでのみ実行
      if (authMethod !== 'qr' && authMethod !== 'qr_face' && authMethod !== 'pin') return;
      
      try {
        const response = await axios.get(`${API}/verify-company?company_code=${companyCode}`);
        if (response.data.company) {
          setCompanyInfo(response.data.company);
        }
      } catch (error) {
        console.error('Company info fetch error:', error);
      }
    };
    loadCompanyInfo();
  }, [companyCode, authMethod]);

  // 従業員データ読み込み（顔認証モード専用）
  useEffect(() => {
    const loadEmployees = async () => {
      // QR認証モードでは顔データロード不要
      if (authMethod === 'qr' || authMethod === 'qr_face' || authMethod === 'pin') return;
      if (!modelsLoaded || !companyCode) return;
      
      setScanStatus("loading");
      
      try {
        // 認証不要の公開エンドポイントを使用（企業コードが必要）
        let employees = [];
        let companyData = null;
        
        try {
          const response = await axios.get(`${API}/verify-company?company_code=${companyCode}`);
          
          // Store company info immediately
          if (response.data.company) {
            companyData = response.data.company;
            setCompanyInfo(companyData);
          }
          
          employees = response.data.employees || [];
          
          // オンラインの場合、IndexedDBにキャッシュ
          if (employees.length > 0) {
            try {
              await saveFaceData(companyCode, employees);
              console.log('Face data cached to IndexedDB');
            } catch (cacheErr) {
              console.log('Cache save failed:', cacheErr);
            }
          }
        } catch (fetchErr) {
          console.log('Network error, trying offline cache:', fetchErr);
          
          // オフラインキャッシュから読み込み
          const cachedData = await getFaceData(companyCode);
          if (cachedData && cachedData.length > 0) {
            toast.info("オフラインモード: キャッシュデータを使用");
            
            // キャッシュからdescriptorsを構築
            const offlineDescriptors = cachedData.map(item => ({
              employee: { id: item.employeeId, name: item.employeeName },
              descriptor: new Float32Array(item.descriptor),
              faceId: item.faceId
            }));
            
            setEmployeeDescriptors(offlineDescriptors);
            setScanStatus("waiting");
            setSubscriptionError(null);
            return;
          } else {
            throw new Error("オフラインデータがありません");
          }
        }
        
        // すぐにカメラを起動（特徴量計算はバックグラウンドで）
        setScanStatus("waiting");
        
        // 特徴量を収集（事前計算済みを優先使用）
        const descriptors = [];
        const needsComputation = [];
        
        for (const employee of employees) {
          const faceEncodings = employee.face_encodings || [];
          for (const faceData of faceEncodings) {
            // 事前計算済みのdescriptorがあればそれを使用
            if (faceData.descriptor && Array.isArray(faceData.descriptor)) {
              descriptors.push({
                employee,
                descriptor: new Float32Array(faceData.descriptor),
                faceId: faceData.id
              });
            } else {
              // なければ画像から計算が必要
              const imageData = faceData.image || faceData.encoding;
              if (imageData) {
                needsComputation.push({ employee, imageData, faceId: faceData.id });
              }
            }
          }
        }
        
        console.log(`Pre-computed descriptors: ${descriptors.length}, needs computation: ${needsComputation.length}`);
        
        // 事前計算済みがあればすぐに使用開始
        if (descriptors.length > 0) {
          setEmployeeDescriptors(descriptors);
        }
        
        // 未計算のものはバックグラウンドで計算
        if (needsComputation.length > 0) {
          const computePromises = needsComputation.map(({ employee, imageData, faceId }) =>
            computeDescriptor(imageData).then(descriptor =>
              descriptor ? { employee, descriptor, faceId } : null
            )
          );
          
          const computedResults = await Promise.all(computePromises);
          const computedDescriptors = computedResults.filter(r => r !== null);
          
          // 結合して更新
          setEmployeeDescriptors(prev => [...prev, ...computedDescriptors]);
          console.log(`Total descriptors after computation: ${descriptors.length + computedDescriptors.length}`);
        }
        
        setSubscriptionError(null);
      } catch (error) {
        console.error("Employee load error:", error);
        if (error.response?.status === 403) {
          setSubscriptionError(error.response.data?.detail || "サブスクリプション契約が必要です");
          setScanStatus("subscription_error");
        } else if (error.response?.status === 400 || error.response?.status === 404) {
          setSubscriptionError("企業が見つかりません");
          setScanStatus("subscription_error");
        }
      }
    };
    loadEmployees();
  }, [modelsLoaded, companyCode]);

  const computeDescriptor = async (base64Image) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = async () => {
        try {
          console.log("Computing descriptor for image, size:", img.width, "x", img.height);
          
          // ===== 案4: 顔画像の前処理改善 =====
          // 明るさ・コントラスト自動調整、サイズ正規化
          const preprocessedCanvas = preprocessFaceImage(img);
          
          // 高精度モデルを優先使用
          const detectionOptions = useSsdModel ? SSD_OPTIONS : TINY_OPTIONS;
          const detection = await faceapi
            .detectSingleFace(preprocessedCanvas, detectionOptions)
            .withFaceLandmarks()
            .withFaceDescriptor();
          if (detection?.descriptor) {
            console.log("Descriptor computed successfully, confidence:", detection.detection.score);
            resolve(detection.descriptor);
          } else {
            // 前処理なしでリトライ
            console.log("Retrying without preprocessing...");
            const fallbackDetection = await faceapi
              .detectSingleFace(img, detectionOptions)
              .withFaceLandmarks()
              .withFaceDescriptor();
            if (fallbackDetection?.descriptor) {
              console.log("Fallback descriptor computed, confidence:", fallbackDetection.detection.score);
              resolve(fallbackDetection.descriptor);
            } else {
              console.log("No face detected in registered image");
              resolve(null);
            }
          }
        } catch (e) {
          console.error("Descriptor computation error:", e);
          resolve(null);
        }
      };
      img.onerror = (e) => {
        console.error("Image load error:", e);
        resolve(null);
      };
      // Handle both data URL and raw base64
      if (base64Image.startsWith("data:")) {
        img.src = base64Image;
      } else {
        img.src = `data:image/jpeg;base64,${base64Image}`;
      }
    });
  };

  // カメラ起動（解像度を動的に設定）
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: "user", 
          width: { ideal: deviceConfig.videoWidth },
          height: { ideal: deviceConfig.videoHeight }
        }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setStreaming(true);
        console.log(`Camera started: ${deviceConfig.videoWidth}x${deviceConfig.videoHeight} (${performanceMode} mode)`);
      }
    } catch (err) {
      console.error("Camera error:", err);
      toast.error("カメラにアクセスできません");
    }
  }, [deviceConfig, performanceMode]);

  const stopCamera = useCallback(() => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      setStreaming(false);
    }
  }, []);

  // 画面の表示/非表示状態の監視
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && companyCode && !subscriptionError) {
        // 画面が再表示されたらカメラを確認・再起動
        console.log("Page became visible, checking camera...");
        const video = videoRef.current;
        if (!video?.srcObject || !streaming) {
          startCamera();
        } else {
          // ストリームが生きているか確認
          const tracks = video.srcObject.getTracks();
          const hasActiveTracks = tracks.some(track => track.readyState === 'live');
          if (!hasActiveTracks) {
            console.log("Camera stream died while hidden, restarting...");
            stopCamera();
            setTimeout(() => startCamera(), 300);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [companyCode, subscriptionError, streaming, startCamera, stopCamera]);

  // カメラ状態の定期チェック（30秒ごと）
  useEffect(() => {
    if (!streaming || !companyCode) return;

    const checkCameraHealth = async () => {
      const video = videoRef.current;
      
      // カメラストリームが停止しているかチェック
      if (video?.srcObject) {
        const tracks = video.srcObject.getTracks();
        const hasActiveTracks = tracks.some(track => track.readyState === 'live');
        
        if (!hasActiveTracks) {
          console.log("Camera stream stopped, restarting...");
          stopCamera();
          setTimeout(() => startCamera(), 500);
          return;
        }
      }

      // 60秒以上検出がない場合はカメラを再起動
      const timeSinceLastDetection = Date.now() - lastSuccessfulDetectionRef.current;
      if (timeSinceLastDetection > 60000 && faceDetected === false) {
        console.log("No detection for 60s, restarting camera...");
        stopCamera();
        setTimeout(() => startCamera(), 500);
      }
    };

    cameraCheckIntervalRef.current = setInterval(checkCameraHealth, 30000);

    return () => {
      if (cameraCheckIntervalRef.current) {
        clearInterval(cameraCheckIntervalRef.current);
      }
    };
  }, [streaming, companyCode, faceDetected, startCamera, stopCamera]);

  // カメラは顔認証が選択されたら起動
  useEffect(() => {
    if (companyCode && !subscriptionError && authMethod === 'face') {
      startCamera();
    }
    return () => {
      stopCamera();
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [companyCode, subscriptionError, authMethod, startCamera, stopCamera]);

  // 顔枠を描画
  const drawFaceBox = (detection, isMatched = false, matchedName = null) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (detection) {
      const box = detection.detection.box;
      // ミラー反転を考慮
      const x = canvas.width - box.x - box.width;
      const y = box.y;
      const width = box.width;
      const height = box.height;
      
      // 枠の色（マッチ時は緑、検出時は青）
      ctx.strokeStyle = isMatched ? '#10b981' : '#3b82f6';
      ctx.lineWidth = 3;
      
      // 角丸四角形を描画
      const radius = 10;
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
      ctx.stroke();
      
      // マッチした場合は名前を表示
      if (isMatched && matchedName) {
        ctx.fillStyle = '#10b981';
        ctx.fillRect(x, y - 30, width, 28);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 16px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(matchedName, x + width / 2, y - 10);
      }
      
      // コーナーマーク
      const cornerLength = 20;
      ctx.strokeStyle = isMatched ? '#10b981' : '#3b82f6';
      ctx.lineWidth = 4;
      
      // 左上
      ctx.beginPath();
      ctx.moveTo(x, y + cornerLength);
      ctx.lineTo(x, y);
      ctx.lineTo(x + cornerLength, y);
      ctx.stroke();
      
      // 右上
      ctx.beginPath();
      ctx.moveTo(x + width - cornerLength, y);
      ctx.lineTo(x + width, y);
      ctx.lineTo(x + width, y + cornerLength);
      ctx.stroke();
      
      // 左下
      ctx.beginPath();
      ctx.moveTo(x, y + height - cornerLength);
      ctx.lineTo(x, y + height);
      ctx.lineTo(x + cornerLength, y + height);
      ctx.stroke();
      
      // 右下
      ctx.beginPath();
      ctx.moveTo(x + width - cornerLength, y + height);
      ctx.lineTo(x + width, y + height);
      ctx.lineTo(x + width, y + height - cornerLength);
      ctx.stroke();
    }
  };

  // 顔検出ループ
  useEffect(() => {
    if (!streaming || !modelsLoaded || candidate || result) {
      // キャンバスをクリア
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
      return;
    }

    const detectFace = async () => {
      if (isDetectingRef.current) {
        animationRef.current = requestAnimationFrame(detectFace);
        return;
      }

      // ビデオの状態をより詳細にチェック
      const video = videoRef.current;
      if (!video || video.readyState < 2 || video.paused || video.ended) {
        // ビデオが停止している場合、再生を試みる
        if (video && video.paused && video.srcObject) {
          try {
            await video.play();
          } catch (e) {
            console.log("Video play failed:", e);
          }
        }
        animationRef.current = requestAnimationFrame(detectFace);
        return;
      }

      const now = Date.now();
      if (now - lastDetectionRef.current < deviceConfig.detectionInterval) {
        animationRef.current = requestAnimationFrame(detectFace);
        return;
      }

      isDetectingRef.current = true;
      lastDetectionRef.current = now;

      try {
        // ===== 案5: ハイブリッドモード =====
        // 低スペックモードでも検出は軽量モデル、特徴量抽出は常に高精度
        // 検出には軽量モデルを使用（低スペック時）
        const detectionOptions = performanceMode === 'low' ? TINY_OPTIONS : SSD_OPTIONS;
        
        // 顔検出（軽量モデル可）
        let detection = await faceapi
          .detectSingleFace(videoRef.current, detectionOptions)
          .withFaceLandmarks();
        
        // 顔が検出されたら、特徴量は常に高精度で抽出
        if (detection) {
          // 特徴量抽出は常に実行（高精度モデルの特徴量を使用）
          const fullDetection = await faceapi
            .detectSingleFace(videoRef.current, useSsdModel ? SSD_OPTIONS : TINY_OPTIONS)
            .withFaceLandmarks()
            .withFaceDescriptor();
          
          if (fullDetection) {
            detection = fullDetection;
          } else {
            // フォールバック: 検出のみの場合は特徴量を別途計算
            detection = null;
          }
        }

        if (detection && detection.descriptor) {
          setFaceDetected(true);
          lastSuccessfulDetectionRef.current = Date.now(); // 最後の検出時刻を更新
          
          // 従業員データがない場合は枠だけ表示
          if (employeeDescriptors.length === 0) {
            setScanStatus("no_employees");
            drawFaceBox(detection, false);
            isDetectingRef.current = false;
            animationRef.current = requestAnimationFrame(detectFace);
            return;
          }
          
          setScanStatus("scanning");
          
          // ===== 案3: 複数顔登録の平均距離判定 =====
          // 同一従業員の複数descriptorから平均距離を計算
          const employeeDistances = new Map(); // employeeId -> {employee, distances: []}
          
          for (const { employee, descriptor } of employeeDescriptors) {
            const distance = faceapi.euclideanDistance(detection.descriptor, descriptor);
            
            if (!employeeDistances.has(employee.id)) {
              employeeDistances.set(employee.id, { employee, distances: [] });
            }
            employeeDistances.get(employee.id).distances.push(distance);
          }
          
          // 各従業員の平均距離を計算し、最も近い従業員を見つける
          let bestMatch = null;
          let bestAvgDistance = MATCH_THRESHOLD;
          
          // デバッグ: 上位候補を収集
          const allCandidates = [];
          
          for (const [employeeId, data] of employeeDistances) {
            // 最小距離と平均距離の加重平均（最小距離を重視）
            const minDist = Math.min(...data.distances);
            const avgDist = data.distances.reduce((a, b) => a + b, 0) / data.distances.length;
            // 重み付け: 最小距離70% + 平均距離30%
            const weightedDistance = minDist * 0.7 + avgDist * 0.3;
            
            allCandidates.push({ 
              name: data.employee.name, 
              id: employeeId, 
              distance: weightedDistance.toFixed(3),
              minDist: minDist.toFixed(3)
            });
            
            if (weightedDistance < bestAvgDistance) {
              bestAvgDistance = weightedDistance;
              bestMatch = data.employee;
            }
          }
          
          // デバッグ: 上位5名を表示
          allCandidates.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
          console.log("Top 5 matches:", allCandidates.slice(0, 5).map(c => `${c.name}: ${c.distance} (min: ${c.minDist})`).join(" | "));
          
          if (bestMatch) {
            // 連続マッチングの実装
            const matchResult = { employeeId: bestMatch.id, distance: bestAvgDistance };
            consecutiveMatchesRef.current.push(matchResult);
            
            // 最新の3件のみ保持
            if (consecutiveMatchesRef.current.length > CONSECUTIVE_MATCHES_REQUIRED) {
              consecutiveMatchesRef.current.shift();
            }
            
            // 進捗表示を更新
            const samePersonMatches = consecutiveMatchesRef.current.filter(
              m => m.employeeId === bestMatch.id
            ).length;
            setMatchProgress(samePersonMatches);
            
            // 3回連続で同じ人物にマッチしたか確認
            if (consecutiveMatchesRef.current.length >= CONSECUTIVE_MATCHES_REQUIRED) {
              const allSamePerson = consecutiveMatchesRef.current.every(
                m => m.employeeId === bestMatch.id
              );
              
              if (allSamePerson) {
                // 3回連続マッチ確定 - 緑枠と名前を表示
                drawFaceBox(detection, true, bestMatch.name);
                setScanStatus("matched");
                
                // 従業員の言語を一時的に設定
                if (bestMatch.language) {
                  setEmployeeLang(bestMatch.language);
                  console.log(`Employee language set: ${bestMatch.language}`);
                }
                
                // 平均距離を計算（信頼度表示用）
                const avgDistance = consecutiveMatchesRef.current.reduce(
                  (sum, m) => sum + m.distance, 0
                ) / consecutiveMatchesRef.current.length;
                
                // 顔画像をキャプチャ
                const faceImage = captureFaceImage(detection);
                
                const response = await axios.post(`${API}/face-auth-local`, {
                  employee_id: bestMatch.id
                });
                
                // 顔写真を従業員データに追加
                const employeeWithPhoto = {
                  ...bestMatch,
                  face_photo: response.data.employee?.profile_photo || response.data.employee?.face_photo || bestMatch.face_encodings?.[0]?.image
                };
                
                setCandidate({
                  employee: employeeWithPhoto,
                  confidence: (1 - avgDistance),
                  suggested_action: response.data.suggested_action,
                  faceImage: faceImage
                });
                
                // マッチング状態をリセット
                consecutiveMatchesRef.current = [];
                setMatchProgress(0);
                isDetectingRef.current = false;
                return;
              }
            }
            
            // まだ3回連続に達していない - 黄色枠で進捗表示
            drawFaceBox(detection, true, `${bestMatch.name} (${samePersonMatches}/${CONSECUTIVE_MATCHES_REQUIRED})`);
          } else {
            // マッチしなかった場合、連続カウントをリセット
            consecutiveMatchesRef.current = [];
            setMatchProgress(0);
            // 顔検出したがマッチしない - 青枠
            drawFaceBox(detection, false);
            
            // 顔認証失敗カウントを増加
            setFaceAuthFailCount(prev => {
              const newCount = prev + 1;
              if (newCount >= FACE_AUTH_MAX_FAILS * 10) { // 約10秒間認識されない場合
                setShowFallbackOption(true);
              }
              return newCount;
            });
          }
        } else {
          setFaceDetected(false);
          setScanStatus("waiting");
          // 顔が検出されなかった場合もカウントをリセット
          consecutiveMatchesRef.current = [];
          setMatchProgress(0);
          // 顔がない場合はキャンバスをクリア
          if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          }
        }
      } catch (error) {
        console.error("Detection error:", error);
      }

      isDetectingRef.current = false;
      animationRef.current = requestAnimationFrame(detectFace);
    };

    animationRef.current = requestAnimationFrame(detectFace);
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [streaming, modelsLoaded, candidate, result, employeeDescriptors, deviceConfig, performanceMode, useSsdModel]);

  // 母国語で挨拶を再生（事前生成した音声ファイルを再生）
  const greetingAudioRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // 単一の <audio> を用意し、初回のユーザー操作でモバイル再生をアンロック
  useEffect(() => {
    const audio = new Audio();
    audio.preload = "auto";
    greetingAudioRef.current = audio;

    const unlock = () => {
      if (audioUnlockedRef.current || !greetingAudioRef.current) return;
      const a = greetingAudioRef.current;
      a.src = "/audio/ja_clock_in.mp3";
      a.muted = true;
      const p = a.play();
      if (p && p.then) {
        p.then(() => {
          a.pause();
          a.currentTime = 0;
          a.muted = false;
          audioUnlockedRef.current = true;
          console.log("[Audio] Unlocked for playback");
        }).catch(() => { a.muted = false; });
      }
    };

    window.addEventListener("touchstart", unlock, { passive: true });
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  const speakGreeting = useCallback((language, action) => {
    const supported = ["ja", "en", "zh", "ko", "vi", "pt", "es", "tl", "id", "th", "ne", "my"];
    const lang = supported.includes(language) ? language : "ja";
    const act = action === "clock_out" ? "clock_out" : "clock_in";
    const src = `/audio/${lang}_${act}.mp3`;
    console.log(`[Audio] Play greeting: ${src}`);
    try {
      const a = greetingAudioRef.current || new Audio();
      greetingAudioRef.current = a;
      a.muted = false;
      a.volume = 1.0;
      a.src = src;
      a.currentTime = 0;
      const p = a.play();
      if (p && p.catch) {
        p.catch((err) => console.warn("[Audio] play failed:", err?.name || err));
      }
    } catch (err) {
      console.error("[Audio] Exception:", err);
    }
  }, []);

  const confirmPunch = async () => {
    if (!candidate?.employee?.id) return;
    setConfirming(true);
    
    // 従業員情報を保持（結果画面で言語表示に使用）
    const employeeInfo = candidate.employee;
    
    const punchData = {
      employee_id: candidate.employee.id,
      action: "auto",
      face_image: candidate.faceImage,
      timestamp: new Date().toISOString()
    };
    
    try {
      if (online) {
        // オンライン: 通常の送信
        const response = await axios.post(`${API}/confirm-punch`, punchData);
        // 結果に従業員情報を追加（言語表示用）
        setResult({
          ...response.data,
          employee: employeeInfo
        });
        setCandidate(null);
        if (response.data.success) {
          const action = response.data.action === "clock_in" ? "clock_in" : "clock_out";
          speakGreeting(employeeInfo.language, action);
          toast.success(response.data.message);
        }
      } else {
        // オフライン: ローカルに保存
        await savePendingAttendance({
          ...punchData,
          employeeName: candidate.employee.name,
          companyCode: companyCode
        });
        
        // バックグラウンド同期をリクエスト
        await requestBackgroundSync('sync-attendance');
        
        setResult({
          success: true,
          message: `オフライン打刻: ${candidate.employee.name}さん（後で同期されます）`,
          action: "offline_punch",
          time: new Date().toLocaleTimeString('ja-JP'),
          employee: employeeInfo  // 従業員情報を追加
        });
        setCandidate(null);
        
        speakGreeting(employeeInfo.language, "clock_in");
        toast.info("オフラインで打刻しました。オンライン復帰時に自動同期されます。");
      }
    } catch (error) {
      toast.error("打刻に失敗しました");
    } finally {
      setConfirming(false);
    }
  };

  const rejectCandidate = () => {
    setCandidate(null);
    setScanStatus("waiting");
    lastDetectionRef.current = 0;
    isDetectingRef.current = false;
    consecutiveMatchesRef.current = [];
    setMatchProgress(0);
    setEmployeeLang(null);  // 言語を日本語（デフォルト）に戻す
  };

  const resetAuth = () => {
    setResult(null);
    setCandidate(null);
    setScanStatus("waiting");
    lastDetectionRef.current = 0;
    isDetectingRef.current = false;
    consecutiveMatchesRef.current = [];
    setMatchProgress(0);
    setEmployeeLang(null);  // 言語を日本語（デフォルト）に戻す
    // QRスキャン状態はstopQrScannerで管理するため、ここではリセットしない
    setAutoConfirmCountdown(null);  // 自動確定カウントダウンをリセット
    setAutoCloseCountdown(null);  // 自動閉じるカウントダウンをリセット
  };

  // 自動確定タイマー（1秒）
  useEffect(() => {
    if (candidate && !confirming) {
      // カウントダウン開始
      setAutoConfirmCountdown(1);
      
      const countdownInterval = setInterval(() => {
        setAutoConfirmCountdown(prev => {
          if (prev === null || prev <= 1) {
            clearInterval(countdownInterval);
            return null;
          }
          return prev - 1;
        });
      }, 1000);
      
      // 1秒後に自動確定または閉じる
      const autoConfirmTimer = setTimeout(() => {
        if (candidate && !confirming) {
          if (candidate.suggested_action === "completed") {
            // 本日完了済みの場合は閉じてスキャナー再開
            resetAuth();
            if (authMethod === 'qr') {
              setTimeout(() => {
                startQrScanner();
              }, 300);
            }
          } else {
            // それ以外は確定
            confirmPunch();
          }
        }
      }, 1000);
      
      return () => {
        clearInterval(countdownInterval);
        clearTimeout(autoConfirmTimer);
        setAutoConfirmCountdown(null);
      };
    }
  }, [candidate, confirming]);

  // 結果画面の自動閉じる（3秒）
  const [autoCloseCountdown, setAutoCloseCountdown] = useState(null);
  
  useEffect(() => {
    if (result && !candidate) {
      // カウントダウン開始
      setAutoCloseCountdown(3);
      
      const countdownInterval = setInterval(() => {
        setAutoCloseCountdown(prev => {
          if (prev === null || prev <= 1) {
            clearInterval(countdownInterval);
            return null;
          }
          return prev - 1;
        });
      }, 1000);
      
      // 3秒後に自動で次の人へ
      const autoCloseTimer = setTimeout(() => {
        resetAuth();
        // QR認証モードの場合はスキャナーを再開（少し遅延を入れる）
        if (authMethod === 'qr') {
          setTimeout(() => {
            startQrScanner();
          }, 300);
        }
      }, 3000);
      
      return () => {
        clearInterval(countdownInterval);
        clearTimeout(autoCloseTimer);
        setAutoCloseCountdown(null);
      };
    }
  }, [result, candidate, authMethod]);

  // PIN認証処理
  const handlePinAuth = async (e) => {
    e.preventDefault();
    if (!employeeCodeInput.trim() || !pinInput.trim()) {
      setPinError('従業員コードとPINを入力してください');
      return;
    }
    
    setPinAuthLoading(true);
    setPinError('');
    
    try {
      const response = await axios.post(`${API}/pin-auth`, {
        company_code: companyCode,
        employee_code: employeeCodeInput.trim(),
        pin: pinInput.trim()
      });
      
      if (response.data.success) {
        // 従業員の言語を設定
        if (response.data.employee?.language) {
          setEmployeeLang(response.data.employee.language);
        }
        
        setCandidate({
          employee: response.data.employee,
          confidence: 1.0,  // PIN認証は100%一致
          suggested_action: response.data.suggested_action,
          faceImage: null
        });
        
        // 入力をリセット
        setEmployeeCodeInput('');
        setPinInput('');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'PIN認証に失敗しました';
      setPinError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setPinAuthLoading(false);
    }
  };

  // 認証方法を戻る
  const goBackToMethodSelection = async () => {
    // QRスキャナーを停止
    if (qrScannerRef.current) {
      try {
        await qrScannerRef.current.stop();
        qrScannerRef.current = null;
      } catch (e) {}
    }
    setQrScanning(false);
    
    // カメラを停止
    stopCamera();
    
    // 状態をリセット
    setAuthMethod(null);
    setEmployeeCodeInput('');
    setPinInput('');
    setPinError('');
    setCandidate(null);
    setResult(null);
    setQrIdentifiedEmployee(null);
    setCapturedPhoto(null);
  };

  // QRコードスキャナー開始
  const startQrScanner = async () => {
    // 既にスキャン中の場合はスキップ（状態とrefの両方をチェック）
    if (qrScanning || qrScannerRef.current) {
      console.log("QR Scanner already running, skipping start", { qrScanning, hasRef: !!qrScannerRef.current });
      return;
    }
    
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      
      // DOM要素の存在確認（authMethodに応じて異なる要素を使用）
      const qrReaderId = authMethod === 'qr_face' ? 'qr-reader-face' : 'qr-reader';
      const qrReaderElement = document.getElementById(qrReaderId);
      if (!qrReaderElement) {
        console.log(`${qrReaderId} element not found, retrying later...`);
        setTimeout(() => startQrScanner(), 500);
        return;
      }
      
      // 既存のビデオ要素をクリア（重複防止）
      qrReaderElement.innerHTML = '';
      
      // 先にフラグを立てて二重起動を防ぐ
      setQrScanning(true);
      
      const scanner = new Html5Qrcode(qrReaderId);
      qrScannerRef.current = scanner;
      
      await scanner.start(
        { facingMode: "environment" },
        { fps: 15, qrbox: { width: 250, height: 250 } },
        async (decodedText, decodedResult) => {
          // QRコードが読み取れた
          console.log("QR Code scanned:", decodedText);
          
          // バイブレーションフィードバック
          if (navigator.vibrate) {
            navigator.vibrate(100);
          }
          
          // 成功音を再生
          try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            oscillator.frequency.value = 1200;
            oscillator.type = 'sine';
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.15);
          } catch (e) {
            console.log("Audio feedback not available");
          }
          
          // QRコードの位置情報を取得して枠を表示
          if (decodedResult?.result?.resultPoints) {
            const points = decodedResult.result.resultPoints;
            const minX = Math.min(...points.map(p => p.x));
            const maxX = Math.max(...points.map(p => p.x));
            const minY = Math.min(...points.map(p => p.y));
            const maxY = Math.max(...points.map(p => p.y));
            setQrDetectedBox({ x: minX, y: minY, width: maxX - minX, height: maxY - minY });
          } else {
            setQrDetectedBox({ detected: true });
          }
          
          // 認証モードによってハンドラーを切り替え
          if (authMethod === 'qr_face') {
            await handleQrFaceIdentify(decodedText);
          } else {
            await handleQrCodeScanned(decodedText);
          }
        },
        (errorMessage) => {
          // スキャン中（QR未検出）- 枠をクリア
        }
      );
    } catch (err) {
      console.error("QR Scanner error:", err);
      setQrScanning(false);
      qrScannerRef.current = null;
      // カメラアクセスエラーの場合はトーストを表示
      if (err.name === 'NotAllowedError' || err.name === 'NotFoundError') {
        toast.error("カメラにアクセスできません");
      }
    }
  };

  // QRコードスキャナー停止
  const stopQrScanner = async () => {
    setQrScanning(false);
    if (qrScannerRef.current) {
      try {
        await qrScannerRef.current.stop();
      } catch (e) {
        console.log("QR scanner stop error (ignored):", e);
      }
      qrScannerRef.current = null;
    }
  };

  // QRコード読み取り後の処理
  const handleQrCodeScanned = async (qrData) => {
    await stopQrScanner();
    setPinAuthLoading(true);
    
    // 前面カメラで認証者の写真を撮影
    let capturedFaceImage = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      
      // ビデオ要素を作成して写真を撮影
      const video = document.createElement('video');
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      await video.play();
      
      // 少し待ってからキャプチャ
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      capturedFaceImage = canvas.toDataURL('image/jpeg', 0.7);
      
      // カメラを停止
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      console.log("Front camera capture failed:", err);
      // カメラがなくても認証は続行
    }
    
    try {
      // QRデータ形式: "PRC:employee_id:company_code" または単純な employee_id
      let employeeId = qrData;
      let qrCompanyCode = companyCode;
      
      if (qrData.startsWith("PRC:")) {
        const parts = qrData.split(":");
        employeeId = parts[1];
        if (parts[2]) qrCompanyCode = parts[2];
      }
      
      const response = await axios.post(`${API}/qr-auth`, {
        company_code: qrCompanyCode,
        employee_id: employeeId,
        captured_face_image: capturedFaceImage  // 撮影した写真を送信
      });
      
      if (response.data.success) {
        if (response.data.employee?.language) {
          setEmployeeLang(response.data.employee.language);
        }
        
        setCandidate({
          employee: response.data.employee,
          confidence: 1.0,
          suggested_action: response.data.suggested_action,
          faceImage: capturedFaceImage  // 撮影した写真を保持
        });
        
        toast.success(`${response.data.employee.name}さんを認識しました`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'QRコード認証に失敗しました';
      toast.error(errorMsg);
      // 再スキャン開始
      setTimeout(() => startQrScanner(), 2000);
    } finally {
      setPinAuthLoading(false);
    }
  };

  // QR+顔写真認証: QRコードで従業員を特定
  const handleQrFaceIdentify = async (qrData) => {
    // QRスキャナーを停止
    if (qrScannerRef.current) {
      try {
        await qrScannerRef.current.stop();
        qrScannerRef.current = null;
      } catch (e) {}
    }
    setQrScanning(false);
    setPinAuthLoading(true);
    
    try {
      let employeeId = qrData;
      let qrCompanyCode = companyCode;
      
      if (qrData.startsWith("PRC:")) {
        const parts = qrData.split(":");
        employeeId = parts[1];
        if (parts[2]) qrCompanyCode = parts[2];
      }
      
      // QR認証APIで従業員情報と状態を取得（まだ打刻しない）
      const response = await axios.post(`${API}/qr-auth`, {
        company_code: qrCompanyCode,
        employee_id: employeeId
      });
      
      if (response.data.success) {
        setQrIdentifiedEmployee({
          id: response.data.employee.id,
          name: response.data.employee.name,
          employee_code: response.data.employee.employee_code,
          language: response.data.employee.language,
          face_photo: response.data.employee.face_photo,
          suggested_action: response.data.suggested_action
        });
        toast.success(`${response.data.employee.name}さんを確認しました。顔写真を撮影してください。`);
        // カメラを起動して写真撮影モードへ
        startPhotoCapture();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || '従業員の確認に失敗しました';
      toast.error(errorMsg);
      setTimeout(() => startQrScanner(), 2000);
    } finally {
      setPinAuthLoading(false);
    }
  };

  // 写真撮影用カメラを起動
  const startPhotoCapture = async () => {
    setPhotoCapturing(true);
    setCapturedPhoto(null);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      
      if (photoVideoRef.current) {
        photoVideoRef.current.srcObject = stream;
        await photoVideoRef.current.play();
      }
    } catch (error) {
      console.error("Camera error:", error);
      toast.error("カメラにアクセスできません");
      setPhotoCapturing(false);
    }
  };

  // 写真を撮影
  const capturePhoto = () => {
    if (!photoVideoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = photoVideoRef.current.videoWidth;
    canvas.height = photoVideoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(photoVideoRef.current, 0, 0);
    
    const photoData = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedPhoto(photoData);
    
    // カメラを停止
    if (photoVideoRef.current?.srcObject) {
      photoVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setPhotoCapturing(false);
  };

  // QR+顔写真認証: 写真確認後に打刻
  const confirmQrFaceAuth = async () => {
    if (!qrIdentifiedEmployee || !capturedPhoto) return;
    
    setConfirming(true);
    
    try {
      // 打刻APIを呼び出し
      const response = await axios.post(`${API}/attendance/clock`, {
        company_code: companyCode,
        employee_id: qrIdentifiedEmployee.id,
        action: qrIdentifiedEmployee.suggested_action
      });
      
      if (response.data.success) {
        const lang = qrIdentifiedEmployee.language || 'ja';
        setEmployeeLang(lang);
        speakGreeting(lang, qrIdentifiedEmployee.suggested_action);
        
        setResult({
          action: qrIdentifiedEmployee.suggested_action,
          time_raw: response.data.raw_time,
          time_rounded: response.data.time,
          employee: qrIdentifiedEmployee
        });
        
        toast.success(qrIdentifiedEmployee.suggested_action === "clock_in" ? "出勤を記録しました" : "退勤を記録しました");
        
        // リセット
        setQrIdentifiedEmployee(null);
        setCapturedPhoto(null);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || '打刻に失敗しました';
      toast.error(errorMsg);
    } finally {
      setConfirming(false);
    }
  };

  // QR+顔写真認証をキャンセル
  const cancelQrFaceAuth = () => {
    setQrIdentifiedEmployee(null);
    setCapturedPhoto(null);
    setPhotoCapturing(false);
    
    if (photoVideoRef.current?.srcObject) {
      photoVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    
    // QRスキャンを再開
    startQrScanner();
  };

  // QR認証モードが選択されたときに自動でスキャナーを開始
  useEffect(() => {
    let mounted = true;
    let timer = null;
    
    // QR認証モードで、企業コード確認済み、かつ認証結果がない場合に開始
    if ((authMethod === 'qr' || authMethod === 'qr_face') && companyCode && !candidate && !result) {
      // スキャンが既に開始されている場合はスキップ（状態とrefの両方をチェック）
      if (qrScanning || qrScannerRef.current) {
        console.log("QR Scanner already active in useEffect, skipping");
        return;
      }
      
      // 少し遅延を入れてDOMが準備されてから開始
      timer = setTimeout(() => {
        // 再度チェック（タイマー中に状態が変わる可能性があるため）
        if (mounted && !qrScanning && !qrScannerRef.current) {
          // DOM要素の存在確認（authMethodに応じて異なる要素を使用）
          const qrReaderId = authMethod === 'qr_face' ? 'qr-reader-face' : 'qr-reader';
          const qrReaderElement = document.getElementById(qrReaderId);
          if (qrReaderElement) {
            console.log("Starting QR scanner from useEffect");
            startQrScanner();
          } else {
            console.log(`${qrReaderId} element not found, retrying...`);
            // 要素がない場合は再試行（1回だけ）
            setTimeout(() => {
              if (mounted && document.getElementById(qrReaderId) && !qrScanning && !qrScannerRef.current) {
                startQrScanner();
              }
            }, 500);
          }
        }
      }, 500);
    }
    
    return () => {
      mounted = false;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [authMethod, companyCode, candidate, result]);

  const getStatusInfo = () => {
    // モデル読み込み中
    if (!modelsLoaded) {
      return { text: t.loading, color: "bg-blue-600", icon: <Loader2 className="w-4 h-4 animate-spin" /> };
    }
    // カメラ起動待ち
    if (!streaming) {
      return { text: t.loading, color: "bg-blue-600", icon: <Loader2 className="w-4 h-4 animate-spin" /> };
    }
    
    switch (scanStatus) {
      case "loading":
        return { text: t.loading, color: "bg-blue-600", icon: <Loader2 className="w-4 h-4 animate-spin" /> };
      case "waiting":
        return { text: selectedLang === "ja" ? "顔をカメラに向けてください" : selectedLang === "en" ? "Please face the camera" : selectedLang === "pt" ? "Por favor, olhe para a câmera" : selectedLang === "vi" ? "Vui lòng nhìn vào camera" : selectedLang === "tl" ? "Harapin ang camera" : "Por favor, mire a la cámara", color: "bg-emerald-600", icon: null };
      case "scanning":
        return { text: t.recognizing, color: "bg-blue-600", icon: <Loader2 className="w-4 h-4 animate-spin" /> };
      case "matched":
        return { text: t.success, color: "bg-emerald-600", icon: <Check className="w-4 h-4" /> };
      case "no_employees":
        return { text: selectedLang === "ja" ? "従業員が未登録です" : "No employees registered", color: "bg-yellow-600", icon: <AlertTriangle className="w-4 h-4" /> };
      case "subscription_error":
        return { text: selectedLang === "ja" ? "利用できません" : "Not available", color: "bg-red-600", icon: <Lock className="w-4 h-4" /> };
      case "error":
        return { text: t.error, color: "bg-red-600", icon: <X className="w-4 h-4" /> };
      default:
        return { text: selectedLang === "ja" ? "準備完了" : "Ready", color: "bg-emerald-600", icon: null };
    }
  };

  const statusInfo = getStatusInfo();

  // Show company code input screen if no code provided
  if (!companyCode) {
    return (
      <div className="min-h-screen bg-slate-900 text-white">
        <header className="bg-slate-800 border-b border-slate-700">
          <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
            <button onClick={() => navigate("/")} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span className="font-bold">PRC勤怠</span>
            </div>
            <div className="w-5" />
          </div>
        </header>
        <main className="max-w-md mx-auto px-4 py-12">
          <Card className="bg-slate-800 border-slate-700 text-center">
            <CardContent className="p-8">
              {/* Language Selector */}
              <div className="flex justify-end mb-4" ref={langDropdownRef}>
                <button
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className="flex items-center gap-2 text-sm bg-slate-700 border border-slate-600 rounded-lg px-3 py-2"
                >
                  <Flag countryCode={LANGUAGES.find(l => l.code === selectedLang)?.countryCode || 'jp'} size={16} />
                  <ChevronDown className={`w-3 h-3 transition-transform ${langDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {langDropdownOpen && (
                  <div className="absolute mt-10 w-40 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-50">
                    {LANGUAGES.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => { setSelectedLang(lang.code); setLangDropdownOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg ${selectedLang === lang.code ? 'bg-slate-700' : ''}`}
                      >
                        <Flag countryCode={lang.countryCode} size={16} />
                        <span className="text-sm text-white">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold mb-2 text-white">{t.faceAuthTitle}</h2>
              <p className="text-slate-400 mb-6">{t.enterCompanyCode}</p>
              
              <form onSubmit={handleCodeSubmit} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    value={inputCode}
                    onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                    placeholder={selectedLang === "ja" ? "例: PRC0001" : "e.g. PRC0001"}
                    className="bg-slate-700 border-slate-600 text-white text-center text-lg tracking-wider"
                    maxLength={10}
                    data-testid="company-code-input"
                  />
                  {codeError && (
                    <p className="text-red-400 text-sm mt-2">{codeError}</p>
                  )}
                </div>
                <Button 
                  type="submit" 
                  disabled={checkingCode || !inputCode.trim()}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="company-code-submit"
                >
                  {checkingCode ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  {t.startFaceAuth}
                </Button>
              </form>
              
              <div className="mt-6 pt-6 border-t border-slate-700">
                <Button variant="outline" onClick={() => navigate("/login")} className="w-full border-slate-600 text-slate-300">
                  {t.adminLogin}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <p className="text-center text-slate-500 text-sm mt-6">
            {selectedLang === "ja" ? "企業コードは管理者にお問い合わせください" : selectedLang === "en" ? "Please contact your administrator for the company code" : selectedLang === "pt" ? "Entre em contato com o administrador para o código da empresa" : selectedLang === "vi" ? "Vui lòng liên hệ quản trị viên để lấy mã công ty" : selectedLang === "tl" ? "Makipag-ugnayan sa admin para sa company code" : "Contacte a su administrador para el código de empresa"}
          </p>
        </main>
      </div>
    );
  }

  // Show subscription error screen
  if (subscriptionError) {
    return (
      <div className="min-h-screen bg-slate-900 text-white">
        <header className="bg-slate-800 border-b border-slate-700">
          <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
            <button onClick={() => navigate("/")} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span className="font-bold">PRC勤怠</span>
            </div>
            <div className="w-5" />
          </div>
        </header>
        <main className="max-w-md mx-auto px-4 py-12">
          <Card className="bg-red-900/30 border-red-500 text-center">
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold mb-2 text-red-400">顔認証が利用できません</h2>
              <p className="text-slate-300 mb-6">{subscriptionError}</p>
              <div className="space-y-3">
                <Button onClick={() => { setCompanyCode(''); setSubscriptionError(null); localStorage.removeItem('prc_company_code'); setInputCode(''); }} className="w-full bg-slate-600 hover:bg-slate-700">
                  別の企業コードを入力
                </Button>
                <Button onClick={() => navigate("/login")} className="w-full bg-emerald-600 hover:bg-emerald-700">
                  管理者ログイン
                </Button>
                <Button variant="outline" onClick={() => navigate("/")} className="w-full border-slate-600">
                  トップに戻る
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // 認証方法選択画面
  if (companyCode && !authMethod) {
    return (
      <div className="min-h-screen bg-slate-900 text-white">
        <header className="bg-slate-800 border-b border-slate-700">
          <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
            <button onClick={() => { setCompanyCode(''); setSearchParams({}); }} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <img src={logoUrl} alt="PRC勤怠" className="w-6 h-6 object-contain rounded-lg" />
              <span className="font-bold">PRC勤怠</span>
            </div>
            <div className="w-5" />
          </div>
        </header>
        <main className="max-w-md mx-auto px-4 py-8">
          {/* 会社名表示 */}
          {companyInfo && (
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full">
                <Building2 className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 font-medium">{companyInfo.name}</span>
              </div>
            </div>
          )}

          {/* 時刻表示 */}
          <div className="text-center mb-8">
            <div className="text-5xl font-bold font-mono">{currentTime}</div>
          </div>

          {/* 認証方法選択 */}
          <h2 className="text-xl font-bold text-center mb-6">
            {selectedLang === "ja" ? "認証方法" : "Authentication Method"}
          </h2>
          
          <div className="flex justify-center">
            {/* QRコード認証 */}
            <button
              onClick={() => setAuthMethod('qr')}
              className="flex flex-col items-center justify-center p-8 bg-slate-800 border-2 border-slate-700 rounded-xl hover:border-purple-500 hover:bg-slate-700 transition-all w-full max-w-xs"
              data-testid="auth-method-qr"
            >
              <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mb-4">
                <QrCode className="w-10 h-10 text-purple-400" />
              </div>
              <span className="text-xl font-bold text-white">
                {selectedLang === "ja" ? "QRコード認証" : "QR Code Auth"}
              </span>
              <span className="text-sm text-slate-400 mt-2">
                {selectedLang === "ja" ? "QRコードをスキャンして打刻" : "Scan QR to clock in/out"}
              </span>
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-700 space-y-3">
            <Button 
              variant="outline" 
              onClick={() => { 
                setCompanyCode(''); 
                localStorage.removeItem('prc_company_code'); 
                setInputCode(''); 
              }} 
              className="w-full border-slate-600 text-slate-400 text-sm"
            >
              {selectedLang === "ja" ? "企業コードを変更" : "Change Company Code"}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate("/login")} 
              className="w-full border-slate-600 text-slate-300"
            >
              {t.adminLogin}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // PIN認証画面
  if (authMethod === 'pin') {
    return (
      <div className="min-h-screen bg-slate-900 text-white">
        <header className="bg-slate-800 border-b border-slate-700">
          <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
            <button onClick={goBackToMethodSelection} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <img src={logoUrl} alt="PRC勤怠" className="w-6 h-6 object-contain rounded-lg" />
              <span className="font-bold">PRC勤怠</span>
            </div>
            <div className="w-5" />
          </div>
        </header>
        <main className="max-w-md mx-auto px-4 py-8">
          {/* 会社名表示 */}
          {companyInfo && (
            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full">
                <Building2 className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 font-medium">{companyInfo.name}</span>
              </div>
            </div>
          )}

          {/* 時刻表示 */}
          <div className="text-center mb-6">
            <div className="text-5xl font-bold font-mono">{currentTime}</div>
          </div>

          {/* 確認画面が表示されていない場合のみPIN入力フォームを表示 */}
          {!candidate && !result && (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-7 h-7 text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-center mb-6 text-white">
                  {selectedLang === "ja" ? "PIN認証" : "PIN Authentication"}
                </h2>
                
                <form onSubmit={handlePinAuth} className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">
                      {selectedLang === "ja" ? "従業員コード" : "Employee Code"}
                    </label>
                    <Input
                      type="text"
                      value={employeeCodeInput}
                      onChange={(e) => setEmployeeCodeInput(e.target.value)}
                      placeholder={selectedLang === "ja" ? "例: 001" : "e.g. 001"}
                      className="bg-slate-700 border-slate-600 text-white text-center text-lg"
                      data-testid="employee-code-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">
                      {selectedLang === "ja" ? "PIN（4〜6桁）" : "PIN (4-6 digits)"}
                    </label>
                    <Input
                      type="password"
                      inputMode="numeric"
                      value={pinInput}
                      onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="••••"
                      className="bg-slate-700 border-slate-600 text-white text-center text-2xl tracking-widest"
                      maxLength={6}
                      data-testid="pin-input"
                    />
                  </div>
                  
                  {pinError && (
                    <p className="text-red-400 text-sm text-center">{pinError}</p>
                  )}
                  
                  <Button 
                    type="submit" 
                    disabled={pinAuthLoading || !employeeCodeInput.trim() || !pinInput.trim()}
                    className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
                    data-testid="pin-submit"
                  >
                    {pinAuthLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Check className="w-5 h-5 mr-2" />
                    )}
                    {selectedLang === "ja" ? "認証" : "Authenticate"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* 確認画面（従業員の言語で表示） */}
          {candidate && !result && (
            <Card className="bg-emerald-900/30 border-emerald-500">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <div className="mx-auto mb-4">
                    <ProfilePhoto employee={candidate.employee} size="w-24 h-24" textSize="text-5xl" />
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-2">{candidate.employee?.name}</h2>
                  <p className="text-slate-300">{candidate.employee?.employee_code}</p>
                </div>

                <div className="text-center mb-6">
                  <p className="text-sm text-slate-400 mb-2">打刻アクション</p>
                  <div className={`inline-flex items-center gap-2 px-6 py-2 rounded-md text-xl font-semibold border-2 ${
                    candidate.suggested_action === "clock_in" 
                      ? "bg-emerald-500/20 text-emerald-400 border-emerald-500" 
                      : candidate.suggested_action === "clock_out"
                      ? "bg-blue-500/20 text-blue-400 border-blue-500"
                      : "bg-slate-600/20 text-slate-400 border-slate-500"
                  }`}>
                    {candidate.suggested_action === "clock_in" && <LogIn className="w-6 h-6" />}
                    {candidate.suggested_action === "clock_out" && <LogOut className="w-6 h-6" />}
                    {candidate.suggested_action === "clock_in" ? et.clockIn : 
                     candidate.suggested_action === "clock_out" ? et.clockOut : 
                     et.completedToday}
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={rejectCandidate} 
                    className="flex-1 h-14 text-base border-slate-400 text-white whitespace-nowrap"
                  >
                    <X className="w-5 h-5 mr-1 flex-shrink-0" /> <span className="truncate">{et.cancel}</span>
                  </Button>
                  {candidate.suggested_action !== "completed" ? (
                    <Button 
                      onClick={confirmPunch} 
                      disabled={confirming} 
                      className="flex-1 h-14 text-base bg-emerald-500 hover:bg-emerald-400 whitespace-nowrap"
                    >
                      {confirming ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Check className="w-5 h-5 mr-1 flex-shrink-0" /> <span className="truncate">{et.confirm}</span>
                          {autoConfirmCountdown !== null && (
                            <span className="ml-1 bg-white/20 px-2 py-0.5 rounded-full text-sm flex-shrink-0">
                              {autoConfirmCountdown}
                            </span>
                          )}
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button onClick={resetAuth} className="flex-1 h-14 text-base whitespace-nowrap">
                      <span className="truncate">{et.close}</span>
                      {autoConfirmCountdown !== null && (
                        <span className="ml-1 bg-white/20 px-2 py-0.5 rounded-full text-sm flex-shrink-0">
                          {autoConfirmCountdown}
                        </span>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* 結果カード */}
          {result && (
            <Card className={`border-2 ${result.action === "clock_in" ? "bg-emerald-900/50 border-emerald-500" : "bg-blue-900/50 border-blue-500"}`}>
              <CardContent className="p-6">
                {/* 挨拶テキスト */}
                <div className="text-center mb-4">
                  <p className={`text-3xl font-bold ${result.action === "clock_in" ? "text-emerald-400" : "text-blue-400"}`}>
                    {result.action === "clock_in" ? et.greetingIn : et.greetingOut}
                  </p>
                </div>
                
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    result.action === "clock_in" ? "bg-emerald-500" : "bg-blue-500"
                  }`}>
                    {result.action === "clock_in" ? <LogIn className="w-8 h-8" /> : <LogOut className="w-8 h-8" />}
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {result.action === "clock_in" ? et.clockInComplete : et.clockOutComplete}
                    </p>
                    <p className="text-slate-300">{result.time_rounded}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button onClick={() => navigate(`/my-report/${result.employee?.id}`)} variant="outline" className="flex-1 h-12">
                    <User className="w-4 h-4 mr-2" /> {et.myPage}
                  </Button>
                  <Button onClick={resetAuth} className="flex-1 h-12">
                    {et.nextPerson}
                    {autoCloseCountdown !== null && (
                      <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-sm">
                        {autoCloseCountdown}
                      </span>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    );
  }

  // QRコード認証画面
  if (authMethod === 'qr') {
    return (
      <div className="min-h-screen bg-slate-900 text-white">
        <main className="max-w-md mx-auto px-4 py-8">
          {/* 会社名表示 */}
          {companyInfo && (
            <div className="text-center mb-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full">
                <Building2 className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 font-medium">{companyInfo.name}</span>
              </div>
            </div>
          )}

          {/* 時刻表示 */}
          <div className="text-center mb-3">
            <div className="text-5xl font-bold font-mono">{currentTime}</div>
          </div>

          {/* 確認画面が表示されていない場合のみQRスキャナーを表示 */}
          {!candidate && !result && (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <QrCode className="w-7 h-7 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-center mb-4 text-white">
                  {selectedLang === "ja" ? "QRコード認証" : "QR Code Authentication"}
                </h2>
                
                {/* QRスキャナー */}
                <div className="relative mb-4">
                  <div id="qr-reader" className="w-full rounded-lg overflow-hidden bg-black" style={{ minHeight: '280px' }}></div>
                  
                  {/* QRコード待機中のガイド枠 - 近未来スタイル */}
                  {qrScanning && !pinAuthLoading && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg">
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36">
                        {/* 外枠 - グロー効果 */}
                        <div 
                          className="absolute inset-0 border border-emerald-400/60 rounded"
                          style={{ boxShadow: '0 0 15px 2px rgba(52, 211, 153, 0.3), inset 0 0 15px 2px rgba(52, 211, 153, 0.1)' }}
                        ></div>
                        
                        {/* コーナーマーク - シャープ */}
                        <div className="absolute -top-1 -left-1 w-6 h-6 border-l-2 border-t-2 border-emerald-400" style={{ boxShadow: '0 0 8px rgba(52, 211, 153, 0.8)' }}></div>
                        <div className="absolute -top-1 -right-1 w-6 h-6 border-r-2 border-t-2 border-emerald-400" style={{ boxShadow: '0 0 8px rgba(52, 211, 153, 0.8)' }}></div>
                        <div className="absolute -bottom-1 -left-1 w-6 h-6 border-l-2 border-b-2 border-emerald-400" style={{ boxShadow: '0 0 8px rgba(52, 211, 153, 0.8)' }}></div>
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 border-r-2 border-b-2 border-emerald-400" style={{ boxShadow: '0 0 8px rgba(52, 211, 153, 0.8)' }}></div>
                        
                        {/* スキャンライン */}
                        <div 
                          className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent"
                          style={{
                            animation: 'scanLine 2s ease-in-out infinite',
                            boxShadow: '0 0 10px 2px rgba(52, 211, 153, 0.6)'
                          }}
                        ></div>
                        
                        {/* 中央のQRアイコン - パルスアニメーション */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div style={{ animation: 'futurePulse 2s ease-in-out infinite' }}>
                            <QrCode className="w-10 h-10 text-emerald-400/40" />
                          </div>
                        </div>
                        
                        {/* 十字線 */}
                        <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-400/20 to-transparent"></div>
                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-emerald-400/20 to-transparent"></div>
                      </div>
                    </div>
                  )}
                  
                  {pinAuthLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
                      <Loader2 className="w-10 h-10 animate-spin text-emerald-400" />
                    </div>
                  )}
                  {!qrScanning && !pinAuthLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
                      <Loader2 className="w-10 h-10 animate-spin text-emerald-400" />
                      <span className="ml-3 text-white">カメラ起動中...</span>
                    </div>
                  )}
                </div>
                
                <p className="text-center text-slate-400 text-xs">
                  {selectedLang === "ja" ? "従業員のQRコードをカメラにかざしてください" : "Hold the employee QR code to the camera"}
                </p>
              </CardContent>
            </Card>
          )}

          {/* 確認画面（従業員の言語で表示） */}
          {candidate && !result && (
            <Card className="bg-emerald-900/30 border-emerald-500">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <div className="mx-auto mb-4">
                    <ProfilePhoto employee={candidate.employee} size="w-24 h-24" textSize="text-5xl" bgColor="from-purple-400 to-purple-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-2">{candidate.employee?.name}</h2>
                  <p className="text-slate-300">{candidate.employee?.employee_code}</p>
                </div>

                <div className="text-center mb-6">
                  <p className="text-sm text-slate-400 mb-2">打刻アクション</p>
                  <div className={`inline-flex items-center gap-2 px-6 py-2 rounded-md text-xl font-semibold border-2 ${
                    candidate.suggested_action === "clock_in" 
                      ? "bg-emerald-500/20 text-emerald-400 border-emerald-500" 
                      : candidate.suggested_action === "clock_out"
                      ? "bg-blue-500/20 text-blue-400 border-blue-500"
                      : "bg-slate-600/20 text-slate-400 border-slate-500"
                  }`}>
                    {candidate.suggested_action === "clock_in" && <LogIn className="w-6 h-6" />}
                    {candidate.suggested_action === "clock_out" && <LogOut className="w-6 h-6" />}
                    {candidate.suggested_action === "clock_in" ? et.clockIn : 
                     candidate.suggested_action === "clock_out" ? et.clockOut : 
                     et.completedToday}
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => { rejectCandidate(); startQrScanner(); }} 
                    className="flex-1 h-14 text-lg border-slate-400 text-white"
                  >
                    <X className="w-5 h-5 mr-2" /> {et.cancel}
                  </Button>
                  {candidate.suggested_action !== "completed" ? (
                    <Button 
                      onClick={confirmPunch} 
                      disabled={confirming} 
                      className="flex-1 h-14 text-lg bg-emerald-500 hover:bg-emerald-400"
                    >
                      {confirming ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Check className="w-5 h-5 mr-2" /> {et.confirm}
                          {autoConfirmCountdown !== null && (
                            <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-sm">
                              {autoConfirmCountdown}
                            </span>
                          )}
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button onClick={resetAuth} className="flex-1 h-14 text-lg">
                      {et.close}
                      {autoConfirmCountdown !== null && (
                        <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-sm">
                          {autoConfirmCountdown}
                        </span>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* 結果カード */}
          {result && (
            <Card className={`border-2 ${result.action === "clock_in" ? "bg-emerald-900/50 border-emerald-500" : "bg-blue-900/50 border-blue-500"}`}>
              <CardContent className="p-6">
                {/* 挨拶テキスト */}
                <div className="text-center mb-4">
                  <p className={`text-3xl font-bold ${result.action === "clock_in" ? "text-emerald-400" : "text-blue-400"}`}>
                    {result.action === "clock_in" ? et.greetingIn : et.greetingOut}
                  </p>
                </div>
                
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    result.action === "clock_in" ? "bg-emerald-500" : "bg-blue-500"
                  }`}>
                    {result.action === "clock_in" ? <LogIn className="w-8 h-8" /> : <LogOut className="w-8 h-8" />}
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {result.action === "clock_in" ? et.clockInComplete : et.clockOutComplete}
                    </p>
                    <p className="text-slate-300">{result.time_rounded}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button onClick={() => navigate(`/my-report/${result.employee?.id}`)} variant="outline" className="flex-1 h-12">
                    <User className="w-4 h-4 mr-2" /> {et.myPage}
                  </Button>
                  <Button onClick={() => { resetAuth(); startQrScanner(); }} className="flex-1 h-12">
                    {et.nextPerson}
                    {autoCloseCountdown !== null && (
                      <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-sm">
                        {autoCloseCountdown}
                      </span>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    );
  }

  // QR+顔写真認証画面
  if (authMethod === 'qr_face') {
    return (
      <div className="min-h-screen bg-slate-900 text-white">
        <header className="bg-slate-800 border-b border-slate-700">
          <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
            <button onClick={goBackToMethodSelection} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <img src={logoUrl} alt="PRC勤怠" className="w-6 h-6 object-contain rounded-lg" />
              <span className="font-bold">PRC勤怠</span>
            </div>
            <div className="w-5" />
          </div>
        </header>
        <main className="max-w-md mx-auto px-4 py-8">
          {/* 会社名表示 */}
          {companyInfo && (
            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600/20 border border-orange-500/30 rounded-full">
                <Building2 className="w-4 h-4 text-orange-400" />
                <span className="text-orange-400 font-medium">{companyInfo.name}</span>
              </div>
            </div>
          )}

          {/* 時刻表示 */}
          <div className="text-center mb-6">
            <div className="text-5xl font-bold font-mono">{currentTime}</div>
          </div>

          {/* Step 1: QRコードスキャン */}
          {!qrIdentifiedEmployee && !result && (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
                  <span className="text-lg font-bold">QRコードをスキャン</span>
                </div>
                
                <div className="relative mb-4">
                  <div id="qr-reader-face" className="w-full rounded-lg overflow-hidden bg-black" style={{ minHeight: '280px' }}></div>
                  {pinAuthLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
                      <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
                    </div>
                  )}
                  {!qrScanning && !pinAuthLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
                      <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
                      <span className="ml-3 text-white">カメラ起動中...</span>
                    </div>
                  )}
                </div>
                
                <p className="text-center text-slate-400 text-sm">
                  従業員のQRコードをかざしてください
                </p>
              </CardContent>
            </Card>
          )}

          {/* Step 2: 顔写真撮影 */}
          {qrIdentifiedEmployee && !capturedPhoto && !result && (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
                  <span className="text-lg font-bold">顔写真を撮影</span>
                </div>
                
                {/* 従業員情報 */}
                <div className="text-center mb-4 p-4 bg-emerald-900/30 rounded-lg border border-emerald-500/30">
                  <div className="mb-2">
                    <ProfilePhoto employee={qrIdentifiedEmployee} size="w-16 h-16" textSize="text-3xl" />
                  </div>
                  <p className="text-xl font-bold text-white">{qrIdentifiedEmployee.name}</p>
                  <p className="text-sm text-slate-400">{qrIdentifiedEmployee.employee_code}</p>
                </div>
                
                {/* カメラプレビュー */}
                <div className="relative mb-4 rounded-lg overflow-hidden bg-black" style={{ minHeight: '280px' }}>
                  <video 
                    ref={photoVideoRef} 
                    autoPlay 
                    playsInline 
                    muted 
                    className="w-full h-full object-cover"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                  {!photoCapturing && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
                      <Button 
                        onClick={startPhotoCapture}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        <ScanFace className="w-5 h-5 mr-2" />
                        カメラ起動
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={cancelQrFaceAuth}
                    className="flex-1 h-12 border-slate-500"
                  >
                    <X className="w-4 h-4 mr-2" /> キャンセル
                  </Button>
                  <Button 
                    onClick={capturePhoto}
                    disabled={!photoCapturing}
                    className="flex-1 h-12 bg-orange-600 hover:bg-orange-700"
                  >
                    <ScanFace className="w-4 h-4 mr-2" /> 撮影
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: 確認画面 */}
          {qrIdentifiedEmployee && capturedPhoto && !result && (
            <Card className="bg-emerald-900/30 border-emerald-500">
              <CardContent className="p-6">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
                  <span className="text-lg font-bold">確認</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* 登録写真 */}
                  <div className="text-center">
                    <p className="text-xs text-slate-400 mb-2">登録写真</p>
                    <ProfilePhoto employee={qrIdentifiedEmployee} size="w-24 h-24" textSize="text-4xl" />
                  </div>
                  {/* 撮影写真 */}
                  <div className="text-center">
                    <p className="text-xs text-slate-400 mb-2">撮影写真</p>
                    <img 
                      src={capturedPhoto} 
                      alt="撮影した写真" 
                      className="w-24 h-24 rounded-full object-cover mx-auto border-2 border-emerald-500"
                      style={{ transform: 'scaleX(-1)' }}
                    />
                  </div>
                </div>
                
                <div className="text-center mb-4">
                  <p className="text-2xl font-bold text-white">{qrIdentifiedEmployee.name}</p>
                  <p className="text-slate-400">{qrIdentifiedEmployee.employee_code}</p>
                </div>
                
                <div className="text-center mb-4">
                  <p className="text-sm text-slate-400 mb-2">打刻アクション</p>
                  <div className={`inline-flex items-center gap-2 px-6 py-2 rounded-md text-xl font-semibold border-2 ${
                    qrIdentifiedEmployee.suggested_action === "clock_in" 
                      ? "bg-emerald-500/20 text-emerald-400 border-emerald-500" 
                      : "bg-blue-500/20 text-blue-400 border-blue-500"
                  }`}>
                    {qrIdentifiedEmployee.suggested_action === "clock_in" && <LogIn className="w-6 h-6" />}
                    {qrIdentifiedEmployee.suggested_action === "clock_out" && <LogOut className="w-6 h-6" />}
                    {qrIdentifiedEmployee.suggested_action === "clock_in" ? "出勤" : "退勤"}
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={cancelQrFaceAuth}
                    className="flex-1 h-14 border-slate-400 text-white"
                  >
                    <X className="w-5 h-5 mr-2" /> やり直し
                  </Button>
                  <Button 
                    onClick={confirmQrFaceAuth}
                    disabled={confirming}
                    className="flex-1 h-14 bg-emerald-500 hover:bg-emerald-400"
                  >
                    {confirming ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" /> 確認
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 結果カード */}
          {result && (
            <Card className={`border-2 ${result.action === "clock_in" ? "bg-emerald-900/50 border-emerald-500" : "bg-blue-900/50 border-blue-500"}`}>
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <p className={`text-3xl font-bold ${result.action === "clock_in" ? "text-emerald-400" : "text-blue-400"}`}>
                    {result.action === "clock_in" ? et.greetingIn : et.greetingOut}
                  </p>
                </div>
                
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    result.action === "clock_in" ? "bg-emerald-500" : "bg-blue-500"
                  }`}>
                    {result.action === "clock_in" ? <LogIn className="w-8 h-8" /> : <LogOut className="w-8 h-8" />}
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {result.action === "clock_in" ? et.clockInComplete : et.clockOutComplete}
                    </p>
                    <p className="text-slate-300">{result.time_rounded}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button onClick={() => navigate(`/my-report/${result.employee?.id}`)} variant="outline" className="flex-1 h-12">
                    <User className="w-4 h-4 mr-2" /> {et.myPage}
                  </Button>
                  <Button onClick={() => { resetAuth(); setAuthMethod('qr_face'); }} className="flex-1 h-12">
                    {et.nextPerson}
                    {autoCloseCountdown !== null && (
                      <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-sm">
                        {autoCloseCountdown}
                      </span>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* ヘッダー */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={goBackToMethodSelection} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src={logoUrl} alt="PRC勤怠" className="w-6 h-6 object-contain rounded-lg" />
            <span className="font-bold">PRC勤怠</span>
          </div>
          <div className="w-5" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6">
        {/* オンライン状態インジケーター */}
        {!online && (
          <div className="flex items-center justify-center gap-2 py-2 px-4 bg-yellow-600/20 border border-yellow-500/30 rounded-lg mb-3">
            <WifiOff className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 text-sm">オフラインモード</span>
          </div>
        )}
        
        {/* 会社名表示 */}
        {companyInfo && (
          <div className="text-center mb-3">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full">
              <Building2 className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 font-medium">{companyInfo.name}</span>
              {online && <Wifi className="w-3 h-3 text-emerald-400" />}
            </div>
          </div>
        )}

        {/* 時刻表示 */}
        <div className="text-center mb-4">
          <div className="text-5xl font-bold font-mono mb-2">{currentTime}</div>
        </div>

        {/* ステータスバー */}
        <div className={`flex items-center justify-center gap-2 py-2 px-4 rounded-lg mb-4 ${statusInfo.color}`}>
          {statusInfo.icon}
          <span className="text-sm font-medium">{statusInfo.text}</span>
        </div>

        {/* カメラ（顔枠オーバーレイ付き） */}
        <div className="relative aspect-[4/3] bg-black rounded-lg overflow-hidden mb-6">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover scale-x-[-1]"
          />
          <canvas
            ref={canvasRef}
            className="absolute top-0 left-0 w-full h-full scale-x-[-1]"
          />
          
          {/* 顔位置ガイドライン */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            {/* 顔の楕円ガイド */}
            <div 
              className={`relative border-4 rounded-full transition-colors duration-300 ${
                faceDetected 
                  ? 'border-emerald-400 shadow-[0_0_20px_rgba(52,211,153,0.5)]' 
                  : 'border-white/50 border-dashed'
              }`}
              style={{ 
                width: '55%', 
                height: '75%',
                maxWidth: '200px',
                maxHeight: '280px'
              }}
            >
              {/* 上部の指示テキスト */}
              {!faceDetected && !candidate && (
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                  <span className="text-white/80 text-sm bg-black/50 px-3 py-1 rounded-full">
                    {selectedLang === "ja" ? "枠内に顔を合わせてください" : "Align your face in the frame"}
                  </span>
                </div>
              )}
              
              {/* コーナーマーク - 左上 */}
              <div className="absolute -top-1 -left-1 w-6 h-6">
                <div className={`absolute top-0 left-0 w-full h-1 ${faceDetected ? 'bg-emerald-400' : 'bg-white'}`}></div>
                <div className={`absolute top-0 left-0 w-1 h-full ${faceDetected ? 'bg-emerald-400' : 'bg-white'}`}></div>
              </div>
              {/* コーナーマーク - 右上 */}
              <div className="absolute -top-1 -right-1 w-6 h-6">
                <div className={`absolute top-0 right-0 w-full h-1 ${faceDetected ? 'bg-emerald-400' : 'bg-white'}`}></div>
                <div className={`absolute top-0 right-0 w-1 h-full ${faceDetected ? 'bg-emerald-400' : 'bg-white'}`}></div>
              </div>
              {/* コーナーマーク - 左下 */}
              <div className="absolute -bottom-1 -left-1 w-6 h-6">
                <div className={`absolute bottom-0 left-0 w-full h-1 ${faceDetected ? 'bg-emerald-400' : 'bg-white'}`}></div>
                <div className={`absolute bottom-0 left-0 w-1 h-full ${faceDetected ? 'bg-emerald-400' : 'bg-white'}`}></div>
              </div>
              {/* コーナーマーク - 右下 */}
              <div className="absolute -bottom-1 -right-1 w-6 h-6">
                <div className={`absolute bottom-0 right-0 w-full h-1 ${faceDetected ? 'bg-emerald-400' : 'bg-white'}`}></div>
                <div className={`absolute bottom-0 right-0 w-1 h-full ${faceDetected ? 'bg-emerald-400' : 'bg-white'}`}></div>
              </div>
            </div>
          </div>
          
          {/* 距離のヒント */}
          {!candidate && (
            <div className="absolute bottom-3 left-0 right-0 flex justify-center">
              <div className="bg-black/70 px-4 py-2 rounded-lg">
                <p className="text-xs text-white/80 text-center">
                  {selectedLang === "ja" 
                    ? "カメラから30〜50cmの距離で" 
                    : "Keep 30-50cm from camera"}
                </p>
              </div>
            </div>
          )}
          {scanStatus === "loading" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70">
              <div className="text-center">
                <Loader2 className="w-10 h-10 animate-spin mx-auto mb-2" />
                <p>モデル読み込み中...</p>
              </div>
            </div>
          )}
          
          {/* 顔検出インジケーター */}
          {!candidate && !result && scanStatus !== "loading" && (
            <div className="absolute bottom-3 left-3 right-3">
              <div className="flex items-center justify-between text-xs">
                <span className={`px-2 py-1 rounded ${faceDetected ? 'bg-blue-600' : 'bg-slate-700'}`}>
                  {faceDetected ? '顔検出中' : '顔未検出'}
                </span>
                <span className={`px-2 py-1 rounded ${performanceMode === 'low' ? 'bg-yellow-600' : 'bg-slate-700'}`}>
                  {performanceMode === 'low' ? '軽量' : '高精度'}
                </span>
                <span className="px-2 py-1 rounded bg-slate-700">
                  {selectedLang === "ja" ? `登録: ${employeeDescriptors.length}名` : `Registered: ${employeeDescriptors.length}`}
                </span>
              </div>
            </div>
          )}
          
          {/* フォールバックオプション - 顔認証がうまくいかない場合 */}
          {showFallbackOption && !candidate && !result && (
            <div className="absolute inset-0 bg-slate-900/90 flex flex-col items-center justify-center p-4 z-20">
              <div className="text-center mb-6">
                <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-white mb-2">
                  {selectedLang === "ja" ? "顔認証がうまくいきません" : "Face auth not working"}
                </h3>
                <p className="text-slate-400 text-sm">
                  {selectedLang === "ja" ? "別の方法で認証してください" : "Please try another method"}
                </p>
              </div>
              
              <div className="flex flex-col gap-3 w-full max-w-xs">
                <Button
                  onClick={() => { setAuthMethod('qr'); setShowFallbackOption(false); setFaceAuthFailCount(0); }}
                  className="h-14 bg-purple-600 hover:bg-purple-700 text-lg"
                >
                  <QrCode className="w-5 h-5 mr-2" />
                  {selectedLang === "ja" ? "QRコード認証に切り替え" : "Switch to QR"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setShowFallbackOption(false); setFaceAuthFailCount(0); }}
                  className="h-12 border-slate-500 text-slate-300"
                >
                  {selectedLang === "ja" ? "顔認証を続ける" : "Continue face auth"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* 確認画面 - 全画面表示（従業員の言語で表示） */}
        {candidate && !result && (
          <div className="fixed inset-0 z-50 bg-gradient-to-b from-emerald-900 to-slate-900 flex flex-col items-center justify-center p-6">
            {/* ヘッダー */}
            <div className="text-center mb-4">
              <span className="inline-block px-6 py-2 bg-emerald-500 text-white text-lg font-bold rounded-full animate-pulse">
                {et.identityConfirm}
              </span>
            </div>

            {/* 従業員情報 */}
            <div className="text-center mb-8">
              <div className="mb-6">
                <ProfilePhoto employee={candidate.employee} size="w-32 h-32" textSize="text-6xl" />
              </div>
              <h2 className="text-5xl sm:text-6xl font-bold text-white mb-3">{candidate.employee?.name}</h2>
              <p className="text-2xl text-slate-300 mb-4">{candidate.employee?.employee_code}</p>
              <div className="inline-block px-6 py-3 bg-emerald-500/20 rounded-xl border border-emerald-500/50">
                <span className="text-emerald-400 text-2xl font-bold">
                  {et.matchRate}: {Math.round((candidate.confidence || 0) * 100)}%
                </span>
              </div>
            </div>

            {/* アクション表示 */}
            <div className="text-center mb-10">
              <p className="text-xl text-slate-400 mb-3">打刻アクション</p>
              <div className={`inline-flex items-center gap-3 px-8 py-4 rounded-lg text-3xl font-semibold border-4 ${
                candidate.suggested_action === "clock_in" 
                  ? "bg-emerald-500/20 text-emerald-400 border-emerald-500" 
                  : candidate.suggested_action === "clock_out"
                  ? "bg-blue-500/20 text-blue-400 border-blue-500"
                  : "bg-slate-600/20 text-slate-400 border-slate-500"
              }`}>
                {candidate.suggested_action === "clock_in" && <LogIn className="w-10 h-10" />}
                {candidate.suggested_action === "clock_out" && <LogOut className="w-10 h-10" />}
                {candidate.suggested_action === "clock_in" ? et.clockIn : 
                 candidate.suggested_action === "clock_out" ? et.clockOut : 
                 et.completedToday}
              </div>
              {candidate.suggested_action !== "completed" && (
                <p className="text-3xl text-white mt-6">
                  {et.confirmQuestion}
                </p>
              )}
            </div>
            
            {/* ボタン */}
            <div className="flex gap-4 w-full max-w-lg px-4">
              <Button 
                variant="outline" 
                onClick={rejectCandidate} 
                className="flex-1 h-20 text-xl border-4 border-slate-400 hover:bg-slate-700 text-white rounded-xl whitespace-nowrap"
              >
                <X className="w-6 h-6 mr-2 flex-shrink-0" /> <span className="truncate">{et.cancel}</span>
              </Button>
              {candidate.suggested_action !== "completed" ? (
                <Button 
                  onClick={confirmPunch} 
                  disabled={confirming} 
                  className="flex-1 h-20 text-xl bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/50 whitespace-nowrap"
                >
                  {confirming ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-6 h-6 mr-2 flex-shrink-0" /> <span className="truncate">{et.confirm}</span>
                    </>
                  )}
                </Button>
              ) : (
                <Button onClick={resetAuth} className="flex-1 h-20 text-xl rounded-xl whitespace-nowrap"><span className="truncate">{et.close}</span></Button>
              )}
            </div>

            {/* 時刻表示 */}
            <div className="absolute top-6 right-6 text-4xl font-mono text-white/80">
              {currentTime}
            </div>
          </div>
        )}

        {/* 結果カード（従業員の言語で表示） */}
        {result && (
          <Card className={`border-2 ${result.action === "clock_in" ? "bg-emerald-900/50 border-emerald-500" : "bg-blue-900/50 border-blue-500"}`}>
            <CardContent className="p-4">
              {/* 挨拶テキスト */}
              <div className="text-center mb-3">
                <p className={`text-2xl font-bold ${result.action === "clock_in" ? "text-emerald-400" : "text-blue-400"}`}>
                  {result.action === "clock_in" ? et.greetingIn : et.greetingOut}
                </p>
              </div>
              
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                  result.action === "clock_in" ? "bg-emerald-500" : "bg-blue-500"
                }`}>
                  {result.action === "clock_in" ? <LogIn className="w-7 h-7" /> : <LogOut className="w-7 h-7" />}
                </div>
                <div>
                  <p className="text-xl font-bold">
                    {result.action === "clock_in" ? et.clockInComplete : et.clockOutComplete}
                  </p>
                  <p className="text-slate-300">{result.time_rounded}</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button onClick={() => navigate(`/my-report/${result.employee?.id}`)} variant="outline" className="flex-1 h-11">
                  <User className="w-4 h-4 mr-1" /> {et.myPage}
                </Button>
                <Button onClick={resetAuth} className="flex-1 h-11">
                  {et.nextPerson}
                  {autoCloseCountdown !== null && (
                    <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-sm">
                      {autoCloseCountdown}
                    </span>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default FaceAuthPage;
